import { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  createFilterOptions,
} from "@mui/material";
import AutoFixHighRoundedIcon from "@mui/icons-material/AutoFixHighRounded";
import { ToolsRunLineItemProps } from "../../../interfaces/interfaces";

type Choice = {
  id?: number;
  name: string;
  partNumber?: string;
  inputValue?: string;
};

export interface ToolsRunDraftOverride {
  rowIndex: number;
  wellId?: number;
  productId?: number;
  createWell?: { name: string };
  createProduct?: { name: string };
}

interface Props {
  row: ToolsRunLineItemProps | null;
  clientName: string;
  wells: Choice[];
  products: Choice[];
  onClose: () => void;
  onSave: (
    override: ToolsRunDraftOverride,
    row: ToolsRunLineItemProps,
  ) => void;
}

const filter = createFilterOptions<Choice>();

const addCreateOption = (
  options: Choice[],
  params: Parameters<typeof filter>[1],
) => {
  const filtered = filter(options, params);
  const name = params.inputValue.trim();
  if (
    name &&
    !options.some((option) => option.name.toLowerCase() === name.toLowerCase())
  ) {
    filtered.push({
      name: "Create “" + name + "”",
      inputValue: name,
    });
  }
  return filtered;
};

const selectedChoice = (value: Choice | null) =>
  value?.inputValue ? { name: value.inputValue } : value;

const optionKey = (option: Choice) =>
  option.id != null ? `id-${option.id}` : `create-${option.inputValue || option.name}`;

export const ToolsRunDraftResolveDialog = ({
  row,
  clientName,
  wells,
  products,
  onClose,
  onSave,
}: Props) => {
  const [well, setWell] = useState<Choice | null>(null);
  const [product, setProduct] = useState<Choice | null>(null);

  useEffect(() => {
    if (!row) return;
    setWell(
      wells.find(
        (option) => option.id === (row.wellId || row.suggestedWellId),
      ) ||
        row.well ||
        null,
    );
    setProduct(
      products.find(
        (option) =>
          option.id === (row.productId || row.suggestedProductIds?.[0]),
      ) ||
        row.product ||
        null,
    );
  }, [products, row, wells]);

  const save = () => {
    if (!row || row.rowIndex == null || !well || !product) return;
    const normalizedWell = selectedChoice(well);
    const normalizedProduct = selectedChoice(product);
    if (!normalizedWell || !normalizedProduct) return;

    const override: ToolsRunDraftOverride = {
      rowIndex: row.rowIndex,
      wellId: normalizedWell.id,
      productId: normalizedProduct.id,
      createWell: normalizedWell.id
        ? undefined
        : { name: normalizedWell.name },
      createProduct: normalizedProduct.id
        ? undefined
        : { name: normalizedProduct.name },
    };
    onSave(override, {
      ...row,
      wellId: normalizedWell.id || null,
      well: {
        id: normalizedWell.id || -1,
        name: normalizedWell.name,
      },
      productId: normalizedProduct.id || null,
      product: {
        id: normalizedProduct.id || -1,
        name: normalizedProduct.name,
        partNumber: normalizedProduct.partNumber || row.partNumber || "",
      },
      status: "matched",
      reviewReason: null,
      suggestedWellId: null,
      suggestedProductIds: null,
    });
  };

  return (
    <Dialog open={Boolean(row)} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 850 }}>
        Resolve shipment match
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            p: 2,
            mb: 2,
            bgcolor: "#F7F6F1",
            border: "1px solid #E9E6DB",
            borderRadius: 2,
          }}
        >
          <Typography variant="overline" color="text.secondary" fontWeight={800}>
            Workbook row
          </Typography>
          <Typography fontWeight={750}>
            {row?.rawShipToWell || "—"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {row?.rawItem || "—"} · Invoice {row?.invoice || "—"}
          </Typography>
        </Box>

        <TextField
          fullWidth
          value={clientName}
          label="Client"
          margin="normal"
          InputProps={{ readOnly: true }}
          helperText="The selected workbook client applies to every row."
        />
        <Autocomplete
          value={well}
          options={wells}
          filterOptions={addCreateOption}
          getOptionLabel={(option) => option.inputValue || option.name}
          isOptionEqualToValue={(option, value) =>
            option.id != null && option.id === value.id
          }
          renderOption={(props, option) => (
            <li {...props} key={optionKey(option)}>
              {option.inputValue || option.name}
            </li>
          )}
          onChange={(_, value) => setWell(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Well"
              margin="normal"
              helperText="Select a well or type a name to stage a new well."
            />
          )}
        />
        <Autocomplete
          value={product}
          options={products}
          filterOptions={addCreateOption}
          getOptionLabel={(option) =>
            option.inputValue ||
            (option.partNumber
              ? option.partNumber + " · " + option.name
              : option.name)
          }
          isOptionEqualToValue={(option, value) =>
            option.id != null && option.id === value.id
          }
          renderOption={(props, option) => (
            <li {...props} key={optionKey(option)}>
              {option.inputValue ||
                (option.partNumber
                  ? option.partNumber + " · " + option.name
                  : option.name)}
            </li>
          )}
          onChange={(_, value) => setProduct(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Tool"
              margin="normal"
              helperText={"Source part number: " + (row?.partNumber || "—")}
            />
          )}
        />
        {row?.partNumber && row?.toolNameRaw && product?.id == null ? (
          <Button
            size="small"
            onClick={() =>
              setProduct({
                name: row.toolNameRaw as string,
                partNumber: row.partNumber as string,
                inputValue: row.toolNameRaw as string,
              })
            }
            sx={{ mt: -1.5, mb: 1, textTransform: "none", fontWeight: 700 }}
          >
            + Create tool "{row.partNumber} · {row.toolNameRaw}" from workbook
          </Button>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!well || !product}
          onClick={save}
          startIcon={<AutoFixHighRoundedIcon />}
        >
          Apply to draft
        </Button>
      </DialogActions>
    </Dialog>
  );
};
